/**
 * @module LanguageTypes
 *
 * The locale vocabulary and the pure helpers that validate it.
 *
 * **This module must never import anything.** It is the leaf of the language import graph, and
 * that is the whole point of it existing separately from `@/i18n/Languages`.
 *
 * `Languages.ts` imports `@/plugins/vuetify` and `@/store/modules/LanguageVuexModule`, and both of
 * those import back — a cycle. Whichever module the bundler enters first, one of them runs while
 * the others are still half-evaluated, so any binding they export is still `undefined` at that
 * moment. That is harmless for anything read at call time, and fatal for anything read while the
 * modules are still initializing.
 *
 * `LanguageVuexModule` reads exactly such a value: its class property initializer validates the
 * persisted locale as the module is created. When those helpers lived in `Languages.ts`, that
 * initializer called back into a module whose `Language` enum had not been assigned yet, so
 * `Object.values(Language)` threw `TypeError: Cannot convert undefined or null to object` during
 * bootstrap and the app rendered a blank page. It only struck when a locale was actually stored,
 * because the no-value path returns before touching the enum, which is why a cleared browser
 * looked fine and every returning user did not.
 *
 * Keeping the enum and the validators here means the Vuex module depends only on a module with no
 * edges of its own, so there is no order in which it can observe an uninitialized binding.
 */

/**
 * Supported UI languages. The value is the locale code used by `vue-i18n` and stored in
 * localStorage.
 */
export enum Language {
  ENGLISH = "en",
  GERMAN = "de",
  FRENCH = "fr",
  POLISH = "pl",
  /* ITALIAN = "it", */
}

/**
 * Default locale used for company settings and as the fallback whenever no usable locale is
 * available.
 *
 * Defined here rather than in `@/plugins/vuetify` (which re-exports it for its existing callers)
 * so that reading it cannot depend on the plugin having finished initializing.
 */
export const DEFAULT_LOCALE = Language.GERMAN;

/**
 * Type guard for a value that claims to be a `Language` but was not produced by the type system:
 * a locale read back from localStorage, or a `language` field deserialized from Firestore.
 * `CompanySettings.language` is typed non-optional, yet a document written before the field
 * existed yields `undefined` at runtime, and nothing between there and storage rejects it.
 */
export function isSupportedLanguage(value: unknown): value is Language {
  return Object.values(Language).includes(value as Language);
}

/**
 * Parses the locale persisted in localStorage. Returns `null` when the stored value cannot be
 * used, which the caller answers by falling back to the default and dropping the key.
 *
 * This must never throw: the read runs in a Vuex module property initializer, so an exception
 * takes the whole app bootstrap down rather than just the language. That happened once, with the
 * literal text "undefined" in storage after `JSON.stringify(undefined)` was handed to `setItem`.
 */
export function parseStoredLocale(raw: string | null): Language | null {
  if (raw === null) {
    return null;
  }

  // Values are written with JSON.stringify, but a bare locale is accepted as well so that
  // anything an older build may have stored still loads.
  let parsed: unknown = raw;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // Not JSON, so judge the raw string on its own instead.
  }

  return isSupportedLanguage(parsed) ? parsed : null;
}
